import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import app from '../common/app';
export function payloadIsPlural(payload) {
  return Array.isArray(payload.data);
}

/**
 * The `Store` class defines a local data store, and provides methods to
 * retrieve data from the API.
 */
export default class Store {
  constructor(models) {
    /**
     * The local data store. A tree of resource types to IDs, such that
     * accessing data[type][id] will return the model for that type/ID.
     */
    _defineProperty(this, "data", {});
    /**
     * The model registry. A map of resource types to the model class that
     * should be used to represent resources of that type.
     */
    _defineProperty(this, "models", void 0);
    this.models = models;
  }

  /**
   * Push resources contained within an API payload into the store.
   *
   * @return The model(s) representing the resource(s) contained
   *     within the 'data' key of the payload.
   */

  pushPayload(payload) {
    if (payload.included) payload.included.map(this.pushObject.bind(this));
    const models = payload.data instanceof Array ? payload.data.map(o => this.pushObject(o)) : this.pushObject(payload.data);
    const result = models;

    // Attach the original payload to the model that we give back. This is
    // useful to consumers as it allows them to access meta information
    // associated with their request.
    result.payload = payload;
    return result;
  }

  /**
   * Create a model to represent a resource object (or update an existing one),
   * and push it into the store.
   *
   * @param data The resource object
   * @return The model, or null if no model class has been
   *     registered for this resource type.
   */

  pushObject(data) {
    if (!this.models[data.type]) {
      setTimeout(() => {
        throw new Error("Pushing object of type `".concat(data.type, "` not allowed, as type not yet registered in the store."));
      });
      return null;
    }
    const type = this.data[data.type] = this.data[data.type] || {};

    // Necessary for TS to narrow correctly.
    const curr = type[data.id];
    const instance = curr ? curr.pushData(data) : this.createRecord(data.type, data);
    type[data.id] = instance;
    instance.exists = true;
    return instance;
  }

  /**
   * Make a request to the API to find record(s) of a specific type.
   */

  async find(type, idOrParams, query, options) {
    var _params;
    if (query === void 0) {
      query = {};
    }
    if (options === void 0) {
      options = {};
    }
    let params = query;
    let url = app.forum.attribute('apiUrl') + '/' + type;
    if (idOrParams instanceof Array) {
      url += '?filter[id]=' + idOrParams.join(',');
    } else if (typeof idOrParams === 'object') {
      params = idOrParams;
    } else if (idOrParams) {
      url += '/' + idOrParams;
    }
    if ('filter' in params && (_params = params) != null && (_params = _params.filter) != null && _params.q) {
      params.filter = app.search.gambits.apply(type, params.filter);
    }
    return app.request(_objectSpread({
      method: 'GET',
      url,
      params
    }, options)).then(payload => {
      if (payloadIsPlural(payload)) {
        return this.pushPayload(payload);
      } else {
        return this.pushPayload(payload);
      }
    });
  }

  /**
   * Get a record from the store by ID.
   */
  getById(type, id) {
    var _this$data;
    return (_this$data = this.data) == null || (_this$data = _this$data[type]) == null ? void 0 : _this$data[id];
  }

  /**
   * Get a record from the store by the value of a model attribute.
   *
   * @param type The resource type.
   * @param key The name of the method on the model.
   * @param value The value of the model attribute.
   */
  getBy(type, key, value) {
    // @ts-expect-error No way to do this safely, unfortunately.
    return this.all(type).filter(model => model[key]() === value)[0];
  }

  /**
   * Get all loaded records of a specific type.
   */
  all(type) {
    const records = this.data[type];
    return records ? Object.values(records) : [];
  }

  /**
   * Remove the given model from the store.
   */
  remove(model) {
    delete this.data[model.data.type][model.id()];
  }

  /**
   * Create a new record of the given type.
   *
   * @param type The resource type
   * @param data Any data to initialize the model with
   */
  createRecord(type, data) {
    if (data === void 0) {
      data = {};
    }
    data.type = data.type || type;

    // @ts-expect-error this will complain about initializing abstract models,
    // but we can safely assume that all models registered with the store are
    // not abstract.
    return new this.models[type](data, this);
  }
}
flarum.reg.add('core', 'common/Store', Store);